import { ProfileImage } from 'components/ProfileImage';
import React, { CSSProperties, useState } from 'react';
import styled, { ThemeProps } from 'styled-components';
import { colors } from 'styleguide/colors';
import { TextArea } from 'styleguide/Inputs';
import ClipLoader from 'react-spinners/ClipLoader';
import { Theme } from 'styleguide/types';
import { MAX_COMMENT_LENGTH } from 'hevy-shared';
import { P2 } from 'styleguide/Texts';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const Container = styled(FlexRow)`
  align-items: flex-start;
  gap: ${Spacing.md}px;
`;

const TextAreaContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const StyledTextArea = styled(TextArea)`
  display: flex;
  flex: 1;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.greyHiperLight : colors.greyUltraDark};
  border-radius: ${CornerRadius.md}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  /* min-height fixes firefox bug where bottom padding is ignored */
  min-height: ${Spacing.md}px;
`;

const PostButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
`;

interface CommentInputProps {
  profilePic?: string;
  onSend: (comment: string) => Promise<void>;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  style?: CSSProperties;
}

export const CommentInput = ({ profilePic, onSend, inputRef, style }: CommentInputProps) => {
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const sendComment = async () => {
    setIsLoading(true);
    try {
      await onSend(comment);
      setComment('');
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container style={style}>
      <ProfileImage source={profilePic} diameter={38} />

      <TextAreaContainer>
        <StyledTextArea
          style={{
            fontWeight: 400,
            // `width: 0` allows input to shrink, nobody knows why
            width: 0,
          }}
          maxRows={10}
          onKeyPress={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              // Prevent dropping a line on enter press, since we'll send the comment.
              event.preventDefault();
              comment && sendComment();
            }
          }}
          ref={inputRef}
          placeholder={t('web.workoutCell.commentInput.writeComment')}
          value={comment}
          disabled={isLoading}
          onChange={e => {
            const comment = e.currentTarget.value;
            if (comment.length > MAX_COMMENT_LENGTH) return;

            setComment(comment);
          }}
        />
        <PostButtonContainer>
          {isLoading ? (
            <ClipLoader size={20} color={colors.primary} loading={true} />
          ) : (
            <P2
              style={{
                cursor: comment !== '' ? 'pointer' : 'default',
                color: comment !== '' ? colors.primary : colors.grey,
              }}
              onClick={() => comment && sendComment()}
            >
              {t('web.workoutCell.commentInput.post')}
            </P2>
          )}
        </PostButtonContainer>
      </TextAreaContainer>
    </Container>
  );
};
